import { CommonModule, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from '@teamfoster/sdk/button';
import { Html5PlayerVars, Html5VideoComponent, Html5VideoPlayerModule } from '@teamfoster/sdk/html5-video-player';
import { IconModule } from '@teamfoster/sdk/icon';
import { ShopifyMedia } from 'src/app/shop/models/Media.model';
import { IconComponent } from '../../../core/components/icon/icon.component';

@Component({
  selector: 'app-shopify-media-item',
  templateUrl: './shopify-media-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, Html5VideoComponent, NgClass],
})
export class ShopifyMediaItemComponent implements OnChanges {
  @Input({ required: true }) mediaItem!: ShopifyMedia;
  @Input() fallbackAlt?: string;
  @Input() alt?: string;
  @Input() previewImage: boolean = false;
  @Input() playVideo: boolean = false;
  @Input() productImage: boolean = true;

  @ViewChild(Html5VideoComponent, { static: false }) video?: Html5VideoComponent;

  playerVars: Html5PlayerVars = {
    muted: true,
    playsinline: 'true',
    loop: true,
    controls: false,
    //autoplay: true,
  };

  // automatic image srcset generation
  @Input() sizes?:
    | {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
        xxl?: number;
      }
    | null
    | undefined = {
    xs: 6,
    sm: 4,
    md: 2,
  };

  // Inherited from CSS: $grid-breakpoints
  // is used to generate size attribute with collspans
  cssBreakPoints = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1400,
  };
  imageSizeKeyPrefix = 'w_';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['playVideo'] && changes['playVideo'].currentValue !== changes['playVideo'].previousValue && !this.previewImage) {
      if (!this.isPlaying && changes['playVideo'].currentValue === true) {
        this.video?.play();
      }
      if (this.isPlaying && changes['playVideo'].currentValue === false) {
        this.video?.pause();
      }
    }
  }

  get isPlaying() {
    const video = this.video?.player;
    if (!video) {
      return false;
    }
    return !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2);
  }

  videoReady(e?: any) {
    if (this.playVideo && !this.previewImage) {
      this.video?.play();
    }
  }

  // ------ automatic image srcset generation

  get imageSizes() {
    return Object.keys(this.mediaItem.previewImage)
      .filter(key => key.startsWith(this.imageSizeKeyPrefix))
      .map(key => parseInt(key.replace(this.imageSizeKeyPrefix, '')));
  }

  get sizesAttr() {
    if (!this.sizes) {
      return 'auto';
    }

    const sizeRules: string[] = [];

    // Skip xs as its default size
    const sizeKeys = Object.keys(this.sizes).filter(key => key !== 'xs');
    const defaultSize = this.sizes.xs;

    // Sets for each container width an srcset (so for full width images)
    sizeKeys.forEach(key => {
      const size = this.sizes?.[key as keyof typeof this.sizes];

      const breakpoint = this.cssBreakPoints[key as keyof typeof this.cssBreakPoints];

      if (!size || !breakpoint) {
        return;
      }

      const width = this.colAsWidth(size);

      sizeRules.push(`(min-width: ${breakpoint}px) ${width}vw`);
    });

    return sizeRules.join(',').concat(`, ${defaultSize ? this.colAsWidth(defaultSize) + 'vw' : '100vw'}`);
  }

  colAsWidth(size: number) {
    let width = (100 / 12) * size;
    return width; // TODO calculate width with max width of container & vw?
  }

  get srcSetAttr() {
    const srcSetRules: string[] = [];

    this.imageSizes.forEach(imgWidth => {
      srcSetRules.push(
        `${this.mediaItem.previewImage[this.imageSizeKeyPrefix + imgWidth]} ${imgWidth}w` // TODO calculate width with max width of container & vw?
      );
    });

    return srcSetRules.join(',');
  }

  // ------ end automatic image srcset generation
}
